import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const FORCE_DELAY_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Compact lock banner rendered inside the sidebar when the selected person
 * is locked by another user.
 *
 * Props:
 *   lockInfo     { lockedByName }  — info about the lock holder
 *   requestStatus  'pending' | 'granted' | 'denied' | undefined
 *   declineMessage  optional reason the holder gave when declining
 *   onRequest()  — callback to send an access request
 *   onForceAcquire() — callback to force-take the lock
 */
export default function SidebarLockBanner({
  lockInfo,
  requestStatus,
  declineMessage,
  onRequest,
  onCancelRequest,
  onForceAcquire
}) {
  const t = useAutoTranslation();

  const [confirmOpen, setConfirmOpen] = useState(false);

  // Show Force Take only after waiting 10 minutes with no response
  const [forceAllowed, setForceAllowed] = useState(false);
  const forceTimerRef = useRef(null);

  useEffect(() => {
    if (requestStatus === 'pending') {
      setForceAllowed(false);
      forceTimerRef.current = setTimeout(
        () => setForceAllowed(true),
        FORCE_DELAY_MS
      );
    } else {
      setForceAllowed(false);
      clearTimeout(forceTimerRef.current);
    }
    return () => clearTimeout(forceTimerRef.current);
  }, [requestStatus]);

  if (!lockInfo) return null;

  return (
    <>
      <Alert
        severity="warning"
        icon={<LockIcon fontSize="inherit" />}
        sx={{ mx: 1, my: 1, '& .MuiAlert-message': { width: '100%' } }}
      >
        <Typography
          variant="body2"
          gutterBottom
          sx={{
            fontWeight: 600
          }}
        >
          {t('{{lockedByName}} is currently editing this record.', {
            lockedByName: lockInfo.lockedByName
          })}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            mt: 0.5
          }}
        >
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={requestStatus === 'pending'}
            onClick={onRequest}
            startIcon={
              requestStatus === 'pending' ? (
                <CircularProgress size={12} color="inherit" />
              ) : null
            }
          >
            {requestStatus === 'pending'
              ? t('Requesting...')
              : t('Request Access')}
          </Button>
          {requestStatus === 'pending' && (
            <Button
              size="small"
              variant="text"
              color="warning"
              onClick={onCancelRequest}
            >
              {t('Cancel')}
            </Button>
          )}
          {forceAllowed && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => setConfirmOpen(true)}
            >
              {t('Force Take')}
            </Button>
          )}
        </Box>
        {/* Unreachable until TGCLOUD-1770 wired the holder's Release / Decline
            actions — nothing ever emitted a result, so this never rendered. */}
        {requestStatus === 'denied' && (
          <Typography
            variant="caption"
            color="error"
            sx={{
              display: 'block',
              mt: 0.5
            }}
          >
            {declineMessage
              ? t('Your request was declined: {{declineMessage}}', {
                  declineMessage
                })
              : t('Your request was declined.')}
          </Typography>
        )}
      </Alert>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>{t('Force take edit access?')}</DialogTitle>
        {/* TGCLOUD-1711: the theme paints every DialogTitle as a solid bar
            (Dashboard.jsx:162), and MUI zeroes DialogContent's top padding via
            `.MuiDialogTitle-root + &` unless `dividers` is set — two classes, so
            a plain sx class loses on specificity. Without this the body text
            sits flush against the bar. */}
        <DialogContent sx={{ pt: '16px !important' }}>
          <Typography variant="body2">
            {t('This will immediately remove')}{' '}
            <strong>{lockInfo.lockedByName}</strong>
            {t("'s lock. Any unsaved changes they have may be lost.")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined">
            {t('Cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              onForceAcquire();
            }}
          >
            {t('Force Take')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
